import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type { Devise, EtatSalle, StatutOeuvre } from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import type { Pagination } from './types.js';
import {
  appelDeRequete,
  cleMetier,
  dansLaPortee,
  dansLaPorteeCourante,
  precedentDansLaPortee,
} from './cles.js';

export interface OeuvreDeLaSalle {
  id: string;
  titre: string;
  artisteNom: string;
  annee: number | null;
  statut: StatutOeuvre;
  mediaPrincipalId: string | null;
  prixPublicCentimes: number | null;
  devise: Devise;
}
export interface SallePresentee {
  id: string;
  titre: { fr: string; en?: string };
  texte: { fr: string; en?: string } | null;
  oeuvres: OeuvreDeLaSalle[];
  oeuvresManquantes: number;
  contact: { id: string; nomAffiche: string } | null;
  etat: EtatSalle;
  expireLe: string;
  prixVisibles: boolean;
  protegee: boolean;
  hoteLien: string | null;
  lienEmisLe: string | null;
  revoqueeLe: string | null;
  ouvertures: {
    total: number;
    visiteurs: number;
    premiereLe: string | null;
    derniereLe: string | null;
    journal: { date: string; visiteur: string }[];
  };
  creeLe: string;
  modifieLe: string;
}
export interface LienSalle {
  url: string;
  expireLe: string;
}
export interface FiltresSalles {
  etat?: EtatSalle;
  contactId?: string;
  oeuvreId?: string;
  page?: number;
  parPage?: number;
}
export interface CorpsSalle {
  titre: { fr: string; en?: string };
  texte?: { fr: string; en?: string };
  oeuvres: string[];
  contactId?: string | null;
  expireLe?: string;
  prixVisibles?: boolean;
  motDePasse?: string | null;
}
const racine = (galerie: string) => `/g/${encodeURIComponent(galerie)}/viewing-rooms`;
export const viewingRoomsApi = {
  lister: (galerie: string, filtres: FiltresSalles = {}, appel?: AppelApi) => {
    const params = new URLSearchParams();
    if (filtres.etat) params.set('etat', filtres.etat);
    if (filtres.contactId) params.set('contactId', filtres.contactId);
    if (filtres.oeuvreId) params.set('oeuvreId', filtres.oeuvreId);
    params.set('page', String(filtres.page ?? 1));
    params.set('parPage', String(filtres.parPage ?? 50));
    return api.get<{ salles: SallePresentee[]; pagination: Pagination }>(
      `${racine(galerie)}?${params}`,
      { galerie, ...appel },
    );
  },
  obtenir: (galerie: string, id: string, appel?: AppelApi) =>
    api.get<SallePresentee>(`${racine(galerie)}/${encodeURIComponent(id)}`, { galerie, ...appel }),
  creer: (galerie: string, corps: CorpsSalle, appel?: AppelApi) =>
    api.post<SallePresentee>(racine(galerie), corps, { galerie, ...appel }),
  modifier: (galerie: string, id: string, corps: Partial<CorpsSalle>, appel?: AppelApi) =>
    api.patch<SallePresentee>(`${racine(galerie)}/${encodeURIComponent(id)}`, corps, {
      galerie,
      ...appel,
    }),
  lien: (galerie: string, id: string, appel?: AppelApi) =>
    api.post<{ salle: SallePresentee; lien: LienSalle }>(
      `${racine(galerie)}/${encodeURIComponent(id)}/lien`,
      {},
      { galerie, ...appel },
    ),
  revoquer: (galerie: string, id: string, appel?: AppelApi) =>
    api.post<SallePresentee>(
      `${racine(galerie)}/${encodeURIComponent(id)}/revoquer`,
      {},
      { galerie, ...appel },
    ),
};
const cleListe = (galerie: string, filtres: FiltresSalles) =>
  cleMetier(galerie, 'viewing-rooms', 'liste', filtres);
const cleFiche = (galerie: string, id: string) => cleMetier(galerie, 'viewing-rooms', 'fiche', id);
export function useSalles(galerie: string, filtres: FiltresSalles = {}) {
  return useQuery({
    queryKey: cleListe(galerie, filtres),
    queryFn: (contexte) => viewingRoomsApi.lister(galerie, filtres, appelDeRequete(contexte)),
    placeholderData: precedentDansLaPortee(galerie),
  });
}
export function useSalle(galerie: string, id: string | null) {
  return useQuery({
    queryKey: cleFiche(galerie, id ?? ''),
    queryFn: (contexte) => viewingRoomsApi.obtenir(galerie, id!, appelDeRequete(contexte)),
    enabled: id !== null,
  });
}
function useRecevoirSalle(galerie: string) {
  const client = useQueryClient();
  return (salle: SallePresentee) => {
    client.setQueryData(cleFiche(galerie, salle.id), salle);
    void client.invalidateQueries({ queryKey: cleMetier(galerie, 'viewing-rooms') });
  };
}
export function useCreerSalle(galerie: string) {
  const recevoir = useRecevoirSalle(galerie);
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: CorpsSalle, appel) => viewingRoomsApi.creer(galerie, corps, appel),
      onSuccess: recevoir,
    }),
  );
}
export function useModifierSalle(galerie: string) {
  const recevoir = useRecevoirSalle(galerie);
  return useMutation(
    dansLaPortee({
      mutationFn: ({ id, corps }: { id: string; corps: Partial<CorpsSalle> }, appel) =>
        viewingRoomsApi.modifier(galerie, id, corps, appel),
      onSuccess: recevoir,
    }),
  );
}
export function useLienSalle(galerie: string) {
  const recevoir = useRecevoirSalle(galerie);
  return useMutation(
    dansLaPortee({
      // Le secret passe directement à l'état local du dialogue : ni QueryCache
      // ni MutationCache ne contiennent l'URL, même après fermeture.
      mutationFn: async ({
        id,
        recevoirLien,
      }: {
        id: string;
        recevoirLien: (lien: LienSalle) => void;
      }) => {
        const resultat = await dansLaPorteeCourante((appel) =>
          viewingRoomsApi.lien(galerie, id, appel),
        );
        recevoirLien(resultat.lien);
        return resultat.salle;
      },
      onSuccess: recevoir,
    }),
  );
}
export function useRevoquerSalle(galerie: string) {
  const recevoir = useRecevoirSalle(galerie);
  return useMutation(
    dansLaPortee({
      mutationFn: (id: string, appel) => viewingRoomsApi.revoquer(galerie, id, appel),
      onSuccess: recevoir,
    }),
  );
}
